import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface StyledFileInputProps {
  id: string;
  accept: string;
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  label?: string;
  hint?: string;
  placeholder?: string;
  className?: string;
}

export function StyledFileInput({
  id,
  accept,
  value,
  onChange,
  disabled = false,
  label,
  hint,
  placeholder,
  className,
}: StyledFileInputProps): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const triggerInput = useCallback(() => {
    if (!disabled) {
      inputRef.current?.click();
    }
  }, [disabled]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      onChange(file);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        triggerInput();
      }
    },
    [triggerInput]
  );

  const handleChangeClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!disabled) {
        onChange(null);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        inputRef.current?.click();
      }
    },
    [disabled, onChange]
  );

  const chooseLabel = placeholder ?? t("components.styledFileInput.noFileChosen");
  const changeLabel = t("components.styledFileInput.changeFile");

  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <div className={cn("space-y-2", className)}>
      {label != null && (
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
      )}
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={disabled}
        onChange={handleChange}
        aria-describedby={hintId}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={triggerInput}
        onKeyDown={handleKeyDown}
        aria-label={value ? value.name : chooseLabel}
        className={cn(
          "flex min-h-[2.5rem] min-w-0 cursor-pointer items-center gap-3 overflow-hidden rounded-md border border-dashed border-input bg-muted/40 px-3 py-2 transition-colors duration-150 hover:border-primary hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          disabled && "cursor-not-allowed opacity-70"
        )}
      >
        <Upload className="h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          {value ? (
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="min-w-0 truncate text-sm font-medium text-foreground" title={value.name}>
                {value.name}
              </span>
              <button
                type="button"
                onClick={handleChangeClick}
                disabled={disabled}
                className="shrink-0 text-xs text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
              >
                {changeLabel}
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground">
                {t("components.styledFileInput.chooseFile")}
              </p>
              {hint != null && (
                <p id={hintId} className="text-xs text-muted-foreground">
                  {hint}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
